/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import GenericButton from "@ui-components/Button/GenericButton";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericModal from "@ui-components/Modal/GenericModal";
import { ReactComponent as Eye } from "@assets/Icons/Icon_Eye.svg";
import { ReactComponent as EyeSlash } from "@assets/Icons/Icon_EyeSlash.svg";
import { ReactComponent as Copy } from "@assets/Icons/IconCopy.svg";
import { ReactComponent as Info } from "@assets/Icons/Iconinformation.svg"
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Fwaas } from "./FwaasData";
import { fetchCredentials } from "./request/request";
import { useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { AxiosInstance } from "axios";

type Props = {
  isOpen: boolean;
  row: Fwaas | null;
  onClose: () => void;
  api: AxiosInstance;
  fmUrl: string | null;
  isFetchingFm: boolean;
  tenantId: number;
};

type FmCredItem = {
  id: number;
  tenantId: number;
  user: string;
  password?: string;
  createdAt?: string;
  updatedAt?: string;
};

type FmCredsResponse = {
  success?: boolean;
  message?: string;
  data?: {
    items?: FmCredItem[];
  };
};

export default function CredentialsModal({
  isOpen,
  row,
  onClose,
  fmUrl,
  isFetchingFm,
  api,
  tenantId,
}: Props) {

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const [isLoadingCreds, setIsLoadingCreds] = useState(false);
  const [credsError, setCredsError] = useState<string | null>(null);

  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState<"user" | "pass" | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      try {
        setIsLoadingCreds(true);
        setCredsError(null);
        const res = await fetchCredentials(api, tenantId);
        const payload: FmCredsResponse = res?.data ?? {};
        const items = payload?.data?.items ?? [];
        const first = items[0];

        if (!cancelled) {
          if (first) {
            setUsername(first.user ?? "");
            setPassword(first.password ?? "");
          } else {
            setUsername("");
            setPassword("");
            setCredsError("No credentials found for this tenant.");
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          setCredsError(e?.message || "Failed to load credentials.");
          setUsername("");
          setPassword("");
        }
      } finally {
        if (!cancelled) setIsLoadingCreds(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, api, tenantId]);

  const handleOpenServiceMgmt = useCallback(() => {
    if (!fmUrl) return;
    window.open(fmUrl, "_blank", "noopener,noreferrer");
  }, [fmUrl]);

  const handleCopy = useCallback((text: string, which: "user" | "pass") => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(which);
      setTimeout(() => setCopied(null), 1200);
    });
  }, []);

  const serviceBtnDisabled = useMemo(
    () => isFetchingFm || !fmUrl,
    [isFetchingFm, fmUrl]
  );

  // --- Bailout AFTER hooks are set up ---
  if (!isOpen && !row) return null;

  return (
    <GenericModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="text-lg font-semibold text-semantic-text-primary">
          Management
        </span>
      }
    >
      {/* Banner */}
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 my-10 flex items-start gap-3">
        <div className="mt-0.5">
          <Info className="w-5 h-5" />
        </div>
        <div className="flex-1 text-sm text-blue-900">
          To review your Firewall-as-a-Service (FWaaS) configuration, open your administration module. This will launch a new tab where you can view your firewall settings.
          If your role allows, you may also make changes from that page. Please use the username and password provided below this message to sign in.
        </div>
        <div>
          <GenericButton
            size="sm"
            variant="secondary"
            onClick={handleOpenServiceMgmt}
            label={
              serviceBtnDisabled
                ? isFetchingFm
                  ? "Loading..."
                  : "Unavailable"
                : "Access"
            }
            disabled={serviceBtnDisabled}
          />
        </div>
      </div>
      {credsError && (
          <div className="text-sm text-red-600">{credsError}</div>
        )}

      {/* Credentials */}
      <div className="space-y-4">
        <span className="text-lg font-semibold text-semantic-text-primary mb-5">
          Credentials
        </span>
        {isLoadingCreds ? (
          <div className="space-y-4">
          <div>
            <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
            <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
          </div>
          <div>
            <div className="h-4 w-20 bg-gray-200 rounded mb-2" />
            <div className="h-9 w-full bg-gray-200 rounded animate-pulse" />
          </div>
          </div>
        ) : (
          <>
        {/* Username */}
        <div>
          <label className="block text-sm font-medium mb-1">Username</label>
          <div className="relative">
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled
              type="text"
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 pr-20 text-sm"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 text-xs px-2 py-1 border rounded-md hover:bg-gray-100"
              onClick={() => handleCopy(username, "user")}
              title="Copy username"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied === "user" ? "Copied!" : "Copy"}
            </button>
          </div>
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-medium mb-1">Password</label>
          <div className="relative">
            <input
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled
              type={showPassword ? "text" : "password"}
              className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 pr-28 text-sm"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded-md hover:bg-gray-100"
                onClick={() => setShowPassword((s) => !s)}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <>
                    <EyeSlash className="w-3.5 h-3.5" />
                    Hide
                  </>
                ) : (
                  <>
                    <Eye className="w-3.5 h-3.5" />
                    Show
                  </>
                )}
              </button>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs px-2 py-1 border rounded-md hover:bg-gray-100"
                onClick={() => handleCopy(password, "pass")}
                title="Copy password"
              >
                <Copy className="w-3.5 h-3.5" />
                {copied === "pass" ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>
        </div>
        </>
        )}
      </div>

      {/* Actions */}
      <div className="mt-6 flex justify-end space-x-2">
        <GenericButton
          size="sm"
          variant="secondary"
          onClick={onClose}
          label="Close"
        />
      </div>
    </GenericModal>
  );
}